/**
 * A wrapper around the native fetch API that automatically adds the
 * Authorization header and handles 401 Unauthorized responses.
 * @param {string} url The URL to fetch.
 * @param {object} options The options to pass to fetch.
 * @param {function} logout The logout function from AuthContext.
 * @param {string} token The authentication token.
 * @returns {Promise<Response>} A promise that resolves to the fetch response.
 */
export const authFetch = async (url, options = {}, logout, token) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // PRD-037 SECURITY FIX: Removed window.__getAccessToken reference
  // Token must be passed explicitly from AuthContext via getAccessToken()

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const data = await response.json();
      if (data.error === 'Token expired') {
        logout && logout();
        throw new Error('Session expired. Please log in again.');
      }
      // a "cloned" response is needed because the body can only be read once
      return new Response(JSON.stringify(data), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      });
    }
  }

  // PRD-067: Handle structured 403 subscription_required responses
  if (response.status === 403) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const data = await response.json();
      if (data.error === 'subscription_required') {
        // Store reason so the redirect target can display context
        try { sessionStorage.setItem('subscription_block_reason', JSON.stringify(data)); } catch (_) {}
        window.location.href = data.redirect || '/profile?tab=subscriptions';
        // Return a never-resolving promise to prevent callers from processing stale data
        return new Promise(() => {});
      }
      // Non-subscription 403 — return cloned response so callers can read body
      return new Response(JSON.stringify(data), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      });
    }
  }

  return response;
};

/**
 * Report a non-JSON response back to the server logs for diagnostics
 */
export const reportUnexpected = async (url, status, bodyStart) => {
  try {
    const base = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    await fetch(`${base}/api/admin/client-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: 'Unexpected non-JSON response', context: { url, status, bodyStart } })
    });
  } catch (_) {}
};

/**
 * Update the status of an org inventory item
 */
export const updateOrgInventoryStatus = async (orgId, itemId, status, userId, logout, token) => {
  return authFetch(`/api/orgs/${orgId}/inventory/${itemId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ userId, status }),
  }, logout, token);
};

/**
 * Report a sale for an org inventory item (with proof image)
 */
export const reportOrgInventorySale = async (orgId, itemId, saleData, proofImageFile, logout, token) => {
  const formData = new FormData();
  Object.entries(saleData).forEach(([key, value]) => formData.append(key, value));
  if (proofImageFile) formData.append('proof_image', proofImageFile);
  // PRD-037 SECURITY FIX: Token must be passed explicitly from AuthContext
  let headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(`/api/orgs/${orgId}/inventory/${itemId}/sale`, {
    method: 'PUT',
    headers,
    body: formData,
  });
};

/**
 * Fetch org inventory items by status (for Kanban columns)
 */
export const fetchOrgInventoryByStatus = async (orgId, status, logout, token) => {
  return authFetch(`/api/orgs/${orgId}/inventory?status=${status}`, {}, logout, token);
}; 